import React, { useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Plot from './PlotlyCustom';
import { BandParamsType, getDataBandParams, varNameBandParams } from '../../utils/dataBandParams';
import { VarName } from '../../utils/varNames';
import { BandValues } from '../SensorArrayWidgets/base';
import { themeProps } from '../../styles/theme';
import { getDataValueNumber, getDataValueString } from '../HelperComponents/DataValueString';
import {
  setActiveMarker,
  setClickedItem,
  setHighlightedItem,
  setSelectedBand,
} from '../../state/actions';
import {
  getActiveMarker,
  getLocSensorStatusData,
  getMotionThreshold,
  getThemeMode,
} from '../../state/selectors';
import { ThemeMode } from '../../state/types';
import { useAppDispatch, useAppSelector } from '../../state/store';

interface DoughnutPlotProps {
  varName: VarName;
  locMarkerId?: string; // to generate small sized summary ring plot for child locations when called from map
  showHistory?: () => void; // to allow navigation to history content, for instance from dashboard ring plots
}

export function DoughnutPlot({
  varName,
  locMarkerId,
  showHistory,
}: DoughnutPlotProps): JSX.Element {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const motionThreshold = useAppSelector(getMotionThreshold);
  const activeMarker = useAppSelector(getActiveMarker);
  const themeMode = useAppSelector(getThemeMode);
  const locSensorStatusData = useAppSelector(getLocSensorStatusData);
  const isDesktopView = useMediaQuery(theme.breakpoints.up('sm'));

  const showAllLabels = isDesktopView && !locMarkerId;

  const plotSize = useMemo(() => {
    let size = 200;
    if (locMarkerId) size = 60; // need small plot for loc summary
    else if (!isDesktopView) size = 80;
    return size;
  }, [isDesktopView, locMarkerId]);

  const onlineData = useMemo(() => {
    const locVarData = locSensorStatusData.onlineData.get(varName) ?? [];
    let data = locVarData;
    if (locMarkerId)
      data = locVarData.filter(
        (item) =>
          locMarkerId &&
          (locMarkerId === item?.location || item.location?.startsWith(`${locMarkerId}#`))
      );
    return data;
  }, [locMarkerId, locSensorStatusData.onlineData, varName]);

  const offlineData = useMemo(() => {
    const locVarData = locSensorStatusData.offlineData.get(varName) ?? [];
    let data = locVarData;
    if (locMarkerId)
      data = locVarData.filter(
        (item) =>
          locMarkerId &&
          (locMarkerId === item?.location || item.location?.startsWith(`${locMarkerId}#`))
      );
    return data;
  }, [locMarkerId, locSensorStatusData.offlineData, varName]);

  let lineColour = theme.palette.text.secondary;
  // HACK - Manually set the line colour to match the highlighted background
  if (varName === activeMarker) {
    if (themeMode === ThemeMode.dark) {
      lineColour = themeProps.colors.summaryLineDark;
    } else {
      lineColour = themeProps.colors.summaryLineLight;
    }
  }

  // calculate for centered ring plot annotation
  const avgValue = useMemo(() => {
    let avg = onlineData.reduce((acc, curr) => acc + (curr?.value ?? 0), 0) / onlineData.length;

    // for onlineStatus, find the value that is repeating the most
    if (varName === VarName.OnlineStatus) {
      const freqMap = onlineData.reduce((m, { value }) => {
        if (value == null) return m; // skip undefined/null
        m.set(value, (m.get(value) ?? 0) + 1);
        return m;
      }, new Map<number, number>());

      const entries = Array.from(freqMap);

      // avoid reduce() error on empty array
      avg = entries.length === 0 ? 0 : entries.reduce((a, b) => (b[1] > a[1] ? b : a))[0];
    }
    return avg;
  }, [onlineData, varName]);

  const avgValueBand = getDataBandParams(varName, avgValue);
  const varNameBand = varNameBandParams[varName] ?? [];

  const perBandSums = () => {
    const result = varNameBand?.map(({ color }) => ({
      total: 0,
      pct: 0,
      colour: color,
    })) as BandValues[];
    onlineData.forEach(({ value }) => {
      for (let i = 0; i < result.length; i++) {
        if ((value ?? Infinity) <= varNameBand[i].upperBound) {
          result[i].total += 1;
          break;
        }
      }
    });
    // add offline data length to the per band sum array
    result.push({ total: offlineData.length, pct: 0, colour: themeProps.colors.lightGrey });
    return result;
  };

  // Varname band that includes offline band
  const combinedBand = useMemo(() => {
    const bands = [...(varNameBandParams[varName] ?? [])];
    if (bands && varName !== VarName.OnlineStatus) {
      bands.push({
        upperBound: NaN,
        color: themeProps.colors.lightGrey,
        text: 'Offline',
        label: 'Offline',
      });
    }
    return bands;
  }, [varName]);

  const plotData = [
    {
      values: perBandSums().flatMap((item) => item.total),
      labels: combinedBand.flatMap((item) => item.label),
      domain: { column: 0 },
      hole: 0.75,
      type: 'pie',
      marker: {
        colors: perBandSums().flatMap((item) => item.colour),
        line: {
          color: lineColour,
          width: 2,
        },
      },
      textinfo: 'none',
      direction: 'clockwise',
      sort: false,
    },
  ] as Plotly.Data[];

  const plotLayout = {
    title: {
      x: 0.5,
      y: 0.5,
    },
    font: { family: themeProps.fontFamily.body },
    annotations: [
      {
        font: {
          size: 14,
          color: avgValueBand?.color ?? theme.palette.text.primary,
        },
        showarrow: false,
        text: `${showAllLabels ? 'Avg.' : ''}`, // hide label for small plots called from location Id
        x: 0.5,
        y: 0.75,
      },
      {
        font: {
          size: showAllLabels ? 20 : 14,
          color: avgValueBand?.color ?? theme.palette.text.primary,
        },
        showarrow: false,
        text: Number.isNaN(avgValue)
          ? 'N/A'
          : getDataValueString(avgValue, varName, undefined, undefined, showAllLabels),
        x: 0.5,
        y: showAllLabels ? 0.55 : 0.5,
      },
      {
        font: {
          size: 16,
          color: avgValueBand?.color ?? themeProps.colors.grey, // default color grey
        },
        showarrow: false,
        text: showAllLabels ? avgValueBand?.label ?? '' : '',
        x: 0.5,
        y: 0.4,
      },
    ],
    width: plotSize,
    height: plotSize,
    margin: {
      l: locMarkerId ? 0 : 10,
      r: locMarkerId ? 0 : 10,
      b: locMarkerId ? 0 : 10,
      t: locMarkerId ? 0 : 10,
    },
    showlegend: false,
    plot_bgcolor: `${theme.palette.primary.main}00`, // transparent
    paper_bgcolor: `${theme.palette.primary.main}00`,
  } as Plotly.Layout;

  const handleBandSensorSelection = (band: BandParamsType | undefined) => {
    const bandSensor = onlineData.find(
      (item) =>
        band === getDataBandParams(varName, getDataValueNumber(item, motionThreshold) ?? Infinity)
    );
    if (bandSensor) {
      // allow band and sensor selection only for desktop view, in mobile view all container is clickable
      if (isDesktopView) dispatch(setClickedItem({ id: bandSensor.id, varName }));
      if (showHistory) showHistory();
    }
  };

  const handleMouseEvent = (event: Plotly.PlotMouseEvent, allowSensorSelection?: boolean) => {
    if (!locMarkerId) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const bandPos = event.points[0].pointNumbers[0]; // point number is missing in some case, for instance offline band in online status
      if (varName === VarName.OnlineStatus) {
        dispatch(setSelectedBand({ varName, band: varNameBand[bandPos] }));
        if (allowSensorSelection) handleBandSensorSelection(varNameBand[bandPos]);
      } else if (bandPos !== combinedBand.length - 1) {
        dispatch(setSelectedBand({ varName, band: combinedBand[bandPos] }));
        if (allowSensorSelection) handleBandSensorSelection(combinedBand[bandPos]);
      } else dispatch(setSelectedBand(null));
    }
  };

  const handleUnHover = () => {
    if (!locMarkerId) {
      dispatch(setSelectedBand(null));
      dispatch(setHighlightedItem({ id: '' }));
    }
  };

  const handleClick = (event: Plotly.PlotMouseEvent) => {
    dispatch(setActiveMarker(varName));
    handleMouseEvent(event, true);
  };

  return (
    <Plot
      data={plotData}
      layout={plotLayout}
      config={{ displayModeBar: false }}
      useResizeHandler
      onClick={handleClick}
      onHover={(e) => handleMouseEvent(e)}
      onUnhover={handleUnHover}
    />
  );
}
DoughnutPlot.defaultProps = {
  locMarkerId: undefined,
  showHistory: undefined,
};
export default DoughnutPlot;
